/**
 * The binary wire codec — the only place that knows what a byte on the socket
 * means.
 *
 * ===========================================================================
 * ENDIANNESS, STATED ONCE
 * ===========================================================================
 *
 * EVERY multi-byte field in this file is LITTLE-ENDIAN. `DataView` defaults to
 * big-endian, so every read and write below passes the flag explicitly through
 * the `Writer`/`Reader` pair; no call site repeats the decision. Little-endian
 * because every machine that will ever run this — x86, ARM, wasm — is
 * little-endian natively, so the encoder and decoder both compile to a store
 * and a load rather than a byte swap.
 *
 * ===========================================================================
 * THE FIRST BYTE IS ALWAYS THE MESSAGE TYPE
 * ===========================================================================
 *
 * `messageType(buf)` reads it without decoding anything else, so a receiver
 * routes on one byte. That matters more than it looks: a snapshot for a client
 * that has already left, or a control frame arriving on the hot path, must be
 * classifiable before any allocation happens.
 *
 * ===========================================================================
 * QUANTIZATION BUDGETS
 * ===========================================================================
 *
 * Taken from ../net/protocol.ts and honoured exactly:
 *
 *   position     int16 over [-8192, 8192]   -> 0.2500 uu   / 0.1250 uu max err
 *   velocity     int16 over [-6000, 6000]   -> 0.1831 uu/s / 0.0916 uu/s max err
 *   angular vel  int16 over [-8, 8] rad/s   -> 0.000244 rad/s / 0.000122 max err
 *   orientation  smallest-three quaternion, 3 x 10 bits + 2-bit index
 *                                           -> <= 0.29 deg
 *   boost        uint8 over [0, 100]        -> 0.196 max err
 *   booleans     one bitfield per car
 *
 * The angular-velocity range is the one budget protocol.ts does not state. 8
 * rad/s is chosen against CAR_MAX_ANGULAR_SPEED (5.5) and BALL_MAX_ANGULAR_SPEED
 * (6.0) with headroom for a tick in which a dodge torque has been applied but
 * the clamp has not yet run; a range of exactly 6 would clip those.
 *
 * OUT-OF-RANGE VALUES CLAMP, THEY DO NOT WRAP. This is not a detail: an int16
 * that wraps puts a car on the far side of the arena, and the symptom is a
 * teleport rather than an error. Every quantizer below saturates at +/-32767
 * (not -32768), which also keeps the mapping symmetric about zero.
 *
 * ===========================================================================
 * WHY CONTROL MESSAGES ARE JSON
 * ===========================================================================
 *
 * Hello / Welcome / RoomState / Event / Kick are rare (a handful per match) and
 * carry shapes that change while the game is being built — a room has a player
 * list, players have names, mutators are a config object. Hand-rolling a binary
 * codec for that buys nothing measurable and costs a wire-format bug every time
 * a field is added. So they travel as UTF-8 JSON inside a framed envelope
 * (`type`, `byteLength`, body), which keeps them routable by the same first
 * byte and length-delimited so several frames can share one datagram later.
 * Snapshots and inputs — the two things sent tens of times per second — are
 * binary, and they are the entire bandwidth story.
 *
 * ===========================================================================
 * DELTA ENCODING
 * ===========================================================================
 *
 * `Snapshot.baseTick` decides the layout. A keyframe (`baseTick === -1`) writes
 * every field of every car. A delta writes a per-car and per-ball CHANGE MASK
 * and then only the fields the mask names; the receiver takes the rest from the
 * snapshot it holds for `baseTick` (see ./snapshot `mergeSnapshot`).
 *
 * The masks are PER AXIS, not per vector, and that is where the delta actually
 * pays: a car driving on the floor has a constant z, a constant vertical
 * velocity and two near-zero angular components, so nine of its twelve body
 * numbers quantize to the same value they had a snapshot ago. Per-vector masks
 * would send all three because one of them moved.
 *
 * The mask lives on `DeltaCarSnapshot.changed`, an OPTIONAL extension of
 * protocol.ts's `CarSnapshot` declared here rather than there: it is a property
 * of this encoding, not of the contract, and a `Snapshot` built by hand with no
 * masks encodes as a keyframe, which is the safe default.
 */

import {
  ClientMessage,
  ServerMessage,
  type BodySnapshot,
  type CarSnapshot,
  type InputPacket,
  type Snapshot,
} from './protocol';
import type { CarInput } from '../physics/car-state';
import type { Mat3 } from '../physics/math';

// ---------------------------------------------------------------------------
// Quantization
// ---------------------------------------------------------------------------

/** Full-scale of the signed 16-bit quantizers. -32768 is deliberately unused so
 *  the mapping is symmetric and a clamp cannot land on a value that negates to
 *  something out of range. */
const I16_MAX = 32767;

/** Position range, uu. protocol.ts: int16 over [-8192, 8192]. */
export const POSITION_RANGE = 8192;
/** Velocity range, uu/s. protocol.ts: int16 over [-6000, 6000]. */
export const VELOCITY_RANGE = 6000;
/** Angular-velocity range, rad/s. See the header for why 8 and not 6. */
export const ANGULAR_RANGE = 8;
/** Boost is 0..100 in one byte. */
export const BOOST_RANGE = 100;
/**
 * Match clock, seconds, uint16 at 0.1 s.
 *
 * This was 655.35 (0.01 s resolution), and it saturated. Two ways:
 *   - `private` and `freeplay` rooms run `LONG_MATCH_SECONDS = 3600`, so the
 *     HUD froze at 10:56 for the entire match;
 *   - overtime counts UP without limit, so even a 5 min match saturated after
 *     ~11 minutes of extra time.
 *
 * 0.1 s costs nothing real — the clock is rendered as mm:ss — and 6553.5 s is
 * an hour and 49 minutes, comfortably past the longest room. Widening the field
 * to uint32 would have bought precision nobody can see for a byte per snapshot
 * per client.
 */
export const CLOCK_RANGE = 6553.5;
/** Boost-pad respawn timer, seconds, uint16. The longest is 10 s (big pads). */
export const PAD_TIMER_RANGE = 16;

/** Worst-case absolute error of each quantizer. Asserted by test/net/codec. */
export const POSITION_STEP = POSITION_RANGE / I16_MAX;
export const VELOCITY_STEP = VELOCITY_RANGE / I16_MAX;
export const ANGULAR_STEP = ANGULAR_RANGE / I16_MAX;
export const BOOST_STEP = BOOST_RANGE / 255;
/**
 * Worst-case angular error of the smallest-three encoding, radians (0.286 deg).
 *
 * NOT simply the per-component step. The three small components each carry up
 * to 0.00069 of error, and the DROPPED one is reconstructed from them, so its
 * error is amplified by 1/w: at the worst attitude — all four components equal,
 * w = 0.5 — that is 3 * 0.5 * 0.00069 / 0.5 = 0.0021 on its own. Summed in
 * quadrature and doubled for the half-angle convention the bound is 0.0048 rad;
 * a 20,000-sample sweep in test/net/codec.test.ts measures 0.0041.
 */
export const ORIENTATION_MAX_ERROR = 0.005;

/** Signed 16-bit quantizer over [-range, range]. Saturates; never wraps. */
export const quantize16 = (value: number, range: number): number => {
  if (!Number.isFinite(value)) return 0;
  const scaled = Math.round((value / range) * I16_MAX);
  return scaled > I16_MAX ? I16_MAX : scaled < -I16_MAX ? -I16_MAX : scaled;
};

export const dequantize16 = (q: number, range: number): number => (q * range) / I16_MAX;

/** Unsigned 8-bit quantizer over [0, range]. Saturates. */
export const quantizeU8 = (value: number, range: number): number => {
  if (!Number.isFinite(value)) return 0;
  const scaled = Math.round((value / range) * 255);
  return scaled > 255 ? 255 : scaled < 0 ? 0 : scaled;
};

export const dequantizeU8 = (q: number, range: number): number => (q * range) / 255;

export const quantizeU16 = (value: number, range: number): number => {
  if (!Number.isFinite(value)) return 0;
  const scaled = Math.round((value / range) * 65535);
  return scaled > 65535 ? 65535 : scaled < 0 ? 0 : scaled;
};

export const dequantizeU16 = (q: number, range: number): number => (q * range) / 65535;

/** Signed 8-bit quantizer for a controller axis over [-1, 1]. Saturates. */
export const quantizeAxis = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  const scaled = Math.round(value * 127);
  return scaled > 127 ? 127 : scaled < -127 ? -127 : scaled;
};

export const dequantizeAxis = (q: number): number => q / 127;

/** Worst-case absolute error of an axis. */
export const AXIS_STEP = 1 / 127;

// ---------------------------------------------------------------------------
// Car flag bits — protocol.ts `CarSnapshot.flags`
// ---------------------------------------------------------------------------

export const FLAG_ON_GROUND = 1 << 0;
export const FLAG_SUPERSONIC = 1 << 1;
export const FLAG_DEMOLISHED = 1 << 2;
export const FLAG_HAS_FLIP = 1 << 3;
export const FLAG_IS_DODGING = 1 << 4;
export const FLAG_JUMPED = 1 << 5;
/** Every bit the wire carries; anything above this is dropped by the encoder. */
export const FLAG_MASK = 0x3f;

// ---------------------------------------------------------------------------
// Input button bits
// ---------------------------------------------------------------------------

export const BUTTON_JUMP = 1 << 0;
export const BUTTON_BOOST = 1 << 1;
export const BUTTON_HANDBRAKE = 1 << 2;

// ---------------------------------------------------------------------------
// Delta change masks
// ---------------------------------------------------------------------------

export const CAR_POSITION_X = 1 << 0;
export const CAR_POSITION_Y = 1 << 1;
export const CAR_POSITION_Z = 1 << 2;
export const CAR_VELOCITY_X = 1 << 3;
export const CAR_VELOCITY_Y = 1 << 4;
export const CAR_VELOCITY_Z = 1 << 5;
export const CAR_ANGULAR_X = 1 << 6;
export const CAR_ANGULAR_Y = 1 << 7;
export const CAR_ANGULAR_Z = 1 << 8;
export const CAR_ORIENTATION = 1 << 9;
export const CAR_BOOST = 1 << 10;
export const CAR_FLAGS = 1 << 11;
/** Every car field present — what a keyframe means, and the default when a
 *  `Snapshot` carries no mask at all. */
export const CAR_ALL = 0xfff;

export const BALL_POSITION_X = 1 << 0;
export const BALL_POSITION_Y = 1 << 1;
export const BALL_POSITION_Z = 1 << 2;
export const BALL_VELOCITY_X = 1 << 3;
export const BALL_VELOCITY_Y = 1 << 4;
export const BALL_VELOCITY_Z = 1 << 5;
export const BALL_ANGULAR_X = 1 << 6;
export const BALL_ANGULAR_Y = 1 << 7;
export const BALL_ANGULAR_Z = 1 << 8;
export const BALL_ALL = 0x1ff;

/**
 * A `CarSnapshot` that also says which of its fields are meaningful.
 *
 * Declared here rather than in protocol.ts because it describes THIS encoding.
 * `changed` absent means "all fields", so a plain `CarSnapshot` — which is what
 * `captureSnapshot` and any hand-written server code produce — is a complete
 * car, and the failure mode of forgetting the mask is a bigger packet rather
 * than a silently wrong car.
 */
export interface DeltaCarSnapshot extends CarSnapshot {
  changed?: number;
}

/** A `Snapshot` carrying per-field change masks. See the header. */
export interface DeltaSnapshot extends Omit<Snapshot, 'cars'> {
  cars: readonly DeltaCarSnapshot[];
  /** Which ball fields are meaningful. Absent means all. */
  ballChanged?: number;
}

/** Reinterpret a `Snapshot` as one that may carry masks. Pure type narrowing —
 *  a snapshot with no masks reads back as a complete one. */
export const asDeltaSnapshot = (snap: Snapshot): DeltaSnapshot => snap as DeltaSnapshot;

const carMask = (car: CarSnapshot): number =>
  (car as DeltaCarSnapshot).changed ?? CAR_ALL;

const ballMask = (snap: Snapshot): number => (snap as DeltaSnapshot).ballChanged ?? BALL_ALL;

// ---------------------------------------------------------------------------
// Orientation — matrix <-> quaternion <-> smallest three
// ---------------------------------------------------------------------------

/** Identity quaternion (x, y, z, w). Also the placeholder a delta writes for an
 *  orientation it is not sending. */
export const IDENTITY_QUAT: readonly [number, number, number, number] = [0, 0, 0, 1];

/**
 * Rotation matrix (row-major, columns = forward/left/up) to a unit quaternion
 * (x, y, z, w).
 *
 * Shepperd's method: pick the branch whose divisor is largest, so the square
 * root never loses precision near a 180-degree rotation. A naive
 * trace-only formula is numerically fine for the trace > 0 case and catastrophic
 * for the other three, and a car on its roof is exactly the other three.
 */
export const matrixToQuat = (m: Mat3): [number, number, number, number] => {
  const m00 = m[0];
  const m01 = m[1];
  const m02 = m[2];
  const m10 = m[3];
  const m11 = m[4];
  const m12 = m[5];
  const m20 = m[6];
  const m21 = m[7];
  const m22 = m[8];

  const trace = m00 + m11 + m22;
  if (trace > 0) {
    const s = Math.sqrt(trace + 1) * 2;
    return [(m21 - m12) / s, (m02 - m20) / s, (m10 - m01) / s, 0.25 * s];
  }
  if (m00 > m11 && m00 > m22) {
    const s = Math.sqrt(1 + m00 - m11 - m22) * 2;
    return [0.25 * s, (m01 + m10) / s, (m02 + m20) / s, (m21 - m12) / s];
  }
  if (m11 > m22) {
    const s = Math.sqrt(1 + m11 - m00 - m22) * 2;
    return [(m01 + m10) / s, 0.25 * s, (m12 + m21) / s, (m02 - m20) / s];
  }
  const s = Math.sqrt(1 + m22 - m00 - m11) * 2;
  return [(m02 + m20) / s, (m12 + m21) / s, 0.25 * s, (m10 - m01) / s];
};

/** Unit quaternion (x, y, z, w) to a rotation matrix, row-major. */
export const quatToMatrix = (q: readonly [number, number, number, number]): Mat3 => {
  let [x, y, z, w] = q;
  const n = Math.hypot(x, y, z, w);
  if (n > 1e-12) {
    x /= n;
    y /= n;
    z /= n;
    w /= n;
  } else {
    x = 0;
    y = 0;
    z = 0;
    w = 1;
  }

  const xx = x * x;
  const yy = y * y;
  const zz = z * z;
  const xy = x * y;
  const xz = x * z;
  const yz = y * z;
  const wx = w * x;
  const wy = w * y;
  const wz = w * z;

  const m = new Float64Array(9) as Mat3;
  m[0] = 1 - 2 * (yy + zz);
  m[1] = 2 * (xy - wz);
  m[2] = 2 * (xz + wy);
  m[3] = 2 * (xy + wz);
  m[4] = 1 - 2 * (xx + zz);
  m[5] = 2 * (yz - wx);
  m[6] = 2 * (xz - wy);
  m[7] = 2 * (yz + wx);
  m[8] = 1 - 2 * (xx + yy);
  return m;
};

/** 1/sqrt(2) — the largest any of the three SMALL components can be. */
const INV_SQRT2 = Math.SQRT1_2;
const SMALL_MAX = 1023;

/**
 * Smallest-three: 3 x 10 bits plus a 2-bit index, packed into one uint32.
 *
 * The dropped component is the one with the largest magnitude, and it is
 * reconstructed as sqrt(1 - a^2 - b^2 - c^2). Because q and -q are the same
 * rotation, the quaternion is negated when the dropped component is negative,
 * so the reconstructed sign is always positive and needs no bit of its own.
 * The remaining three are then each bounded by 1/sqrt(2), which is what makes
 * 10 bits enough for a quarter of a degree.
 *
 * NOT QUITE IDEMPOTENT, and it cannot be. When two components are within a
 * quantum of each other the reconstruction can make the other one the larger,
 * so `packQuat(unpackQuat(p))` may pick a different 2-bit index and produce
 * different bits for the same rotation. It is the same rotation to 0.005 rad
 * either way, so the only consequence is that a delta occasionally re-sends
 * four bytes of orientation that did not visibly change. Nothing downstream may
 * treat these 32 bits as an identity for a rotation.
 */
export const packQuat = (q: readonly [number, number, number, number]): number => {
  let x = q[0];
  let y = q[1];
  let z = q[2];
  let w = q[3];

  const n = Math.hypot(x, y, z, w);
  if (n > 1e-12) {
    x /= n;
    y /= n;
    z /= n;
    w /= n;
  } else {
    x = 0;
    y = 0;
    z = 0;
    w = 1;
  }

  const a = [x, y, z, w];
  let largest = 0;
  for (let i = 1; i < 4; i++) if (Math.abs(a[i]) > Math.abs(a[largest])) largest = i;
  if (a[largest] < 0) for (let i = 0; i < 4; i++) a[i] = -a[i];

  let packed = largest << 30;
  let shift = 20;
  for (let i = 0; i < 4; i++) {
    if (i === largest) continue;
    const clamped = a[i] < -INV_SQRT2 ? -INV_SQRT2 : a[i] > INV_SQRT2 ? INV_SQRT2 : a[i];
    const q10 = Math.round(((clamped / INV_SQRT2) * 0.5 + 0.5) * SMALL_MAX);
    packed |= (q10 & SMALL_MAX) << shift;
    shift -= 10;
  }
  return packed >>> 0;
};

export const unpackQuat = (packed: number): [number, number, number, number] => {
  const largest = (packed >>> 30) & 3;
  const out: [number, number, number, number] = [0, 0, 0, 0];

  let shift = 20;
  let sumSq = 0;
  for (let i = 0; i < 4; i++) {
    if (i === largest) continue;
    const q10 = (packed >>> shift) & SMALL_MAX;
    const v = ((q10 / SMALL_MAX) * 2 - 1) * INV_SQRT2;
    out[i] = v;
    sumSq += v * v;
    shift -= 10;
  }
  out[largest] = Math.sqrt(Math.max(0, 1 - sumSq));
  return out;
};

// ---------------------------------------------------------------------------
// Byte plumbing. Little-endian is decided here and nowhere else.
// ---------------------------------------------------------------------------

const LE = true;

class Writer {
  private readonly buffer: ArrayBuffer;
  private readonly view: DataView;
  private readonly bytes: Uint8Array;
  private offset = 0;

  constructor(capacity: number) {
    this.buffer = new ArrayBuffer(capacity);
    this.view = new DataView(this.buffer);
    this.bytes = new Uint8Array(this.buffer);
  }

  u8(v: number): void {
    this.view.setUint8(this.offset, v & 0xff);
    this.offset += 1;
  }

  i8(v: number): void {
    this.view.setInt8(this.offset, v);
    this.offset += 1;
  }

  u16(v: number): void {
    this.view.setUint16(this.offset, v & 0xffff, LE);
    this.offset += 2;
  }

  i16(v: number): void {
    this.view.setInt16(this.offset, v, LE);
    this.offset += 2;
  }

  u32(v: number): void {
    this.view.setUint32(this.offset, v >>> 0, LE);
    this.offset += 4;
  }

  i32(v: number): void {
    this.view.setInt32(this.offset, v | 0, LE);
    this.offset += 4;
  }

  raw(src: Uint8Array): void {
    this.bytes.set(src, this.offset);
    this.offset += src.length;
  }

  /** The written prefix, copied. Frames are handed to a transport that may keep
   *  them, so they never alias the scratch buffer. */
  finish(): ArrayBuffer {
    return this.buffer.slice(0, this.offset);
  }
}

class Reader {
  private readonly view: DataView;
  private offset = 0;

  constructor(private readonly buf: ArrayBuffer) {
    this.view = new DataView(buf);
  }

  u8(): number {
    const v = this.view.getUint8(this.offset);
    this.offset += 1;
    return v;
  }

  i8(): number {
    const v = this.view.getInt8(this.offset);
    this.offset += 1;
    return v;
  }

  u16(): number {
    const v = this.view.getUint16(this.offset, LE);
    this.offset += 2;
    return v;
  }

  i16(): number {
    const v = this.view.getInt16(this.offset, LE);
    this.offset += 2;
    return v;
  }

  u32(): number {
    const v = this.view.getUint32(this.offset, LE);
    this.offset += 4;
    return v;
  }

  i32(): number {
    const v = this.view.getInt32(this.offset, LE);
    this.offset += 4;
    return v;
  }

  raw(length: number): Uint8Array {
    const v = new Uint8Array(this.buf, this.offset, length);
    this.offset += length;
    return v;
  }
}

// ---------------------------------------------------------------------------
// Framing
// ---------------------------------------------------------------------------

/**
 * The message type of a frame, read without decoding the body.
 *
 * Throws on an empty buffer rather than returning a sentinel: a zero-length
 * frame is a transport bug, and returning 0 would route it to whichever message
 * happens to be numbered 0 later.
 */
export const messageType = (buf: ArrayBuffer): number => {
  if (buf.byteLength < 1) throw new Error('empty frame');
  return new DataView(buf).getUint8(0);
};

// ---------------------------------------------------------------------------
// Snapshots
// ---------------------------------------------------------------------------

/** Header flag bits. Only one so far; the byte exists so adding a second is not
 *  a format break. */
const SNAP_KEYFRAME = 1 << 0;

/** Upper bound on the encoded size, for the scratch allocation. */
const snapshotCapacity = (snap: Snapshot): number =>
  16 + snap.cars.length * 27 + 20 + 6 + 2 + snap.padChanges.length * 4;

const writeVec = (w: Writer, v: readonly [number, number, number], range: number, mask: number, bits: number): void => {
  if (mask & bits) w.i16(quantize16(v[0], range));
  if (mask & (bits << 1)) w.i16(quantize16(v[1], range));
  if (mask & (bits << 2)) w.i16(quantize16(v[2], range));
};

const readVec = (
  r: Reader,
  range: number,
  mask: number,
  bits: number
): [number, number, number] => [
  mask & bits ? dequantize16(r.i16(), range) : 0,
  mask & (bits << 1) ? dequantize16(r.i16(), range) : 0,
  mask & (bits << 2) ? dequantize16(r.i16(), range) : 0,
];

/**
 * Encode a snapshot. Layout, in order, all little-endian:
 *
 *   u8   type            = ServerMessage.Snapshot
 *   u8   flags           bit0 = keyframe
 *   u32  tick
 *   u32  baseTick        DELTA ONLY
 *   i32  ackedInputSeq   -1 for "nothing acked yet"
 *   u8   carCount
 *   per car:
 *     u16  changed mask  DELTA ONLY (a keyframe implies CAR_ALL)
 *     i16  position   x, y, z   each present only if its mask bit is set
 *     i16  velocity   x, y, z
 *     i16  angular    x, y, z
 *     u32  orientation           smallest-three
 *     u8   boost
 *     u8   flags
 *   ball:
 *     u16  changed mask  DELTA ONLY
 *     i16  position / velocity / angular, per mask
 *   match:
 *     u8   phase
 *     u16  clock                 0.1 s
 *     u8   score[0], u8 score[1]
 *   pads:
 *     u8   count
 *     per pad: u8 (index in bits 0..5 | active << 6 | hasTimer << 7)
 *              u16 respawnTimer  only when hasTimer
 */
export const encodeSnapshot = (snap: Snapshot): ArrayBuffer => {
  const keyframe = snap.baseTick < 0;
  const w = new Writer(snapshotCapacity(snap));

  w.u8(ServerMessage.Snapshot);
  w.u8(keyframe ? SNAP_KEYFRAME : 0);
  w.u32(snap.tick);
  if (!keyframe) w.u32(snap.baseTick);
  w.i32(snap.ackedInputSeq);
  w.u8(snap.cars.length);

  for (const car of snap.cars) {
    const mask = keyframe ? CAR_ALL : carMask(car);
    if (!keyframe) w.u16(mask);
    writeVec(w, car.position, POSITION_RANGE, mask, CAR_POSITION_X);
    writeVec(w, car.velocity, VELOCITY_RANGE, mask, CAR_VELOCITY_X);
    writeVec(w, car.angularVelocity, ANGULAR_RANGE, mask, CAR_ANGULAR_X);
    if (mask & CAR_ORIENTATION) w.u32(packQuat(car.orientation));
    if (mask & CAR_BOOST) w.u8(quantizeU8(car.boost, BOOST_RANGE));
    if (mask & CAR_FLAGS) w.u8(car.flags & FLAG_MASK);
  }

  const bmask = keyframe ? BALL_ALL : ballMask(snap);
  if (!keyframe) w.u16(bmask);
  writeVec(w, snap.ball.position, POSITION_RANGE, bmask, BALL_POSITION_X);
  writeVec(w, snap.ball.velocity, VELOCITY_RANGE, bmask, BALL_VELOCITY_X);
  writeVec(w, snap.ball.angularVelocity, ANGULAR_RANGE, bmask, BALL_ANGULAR_X);

  w.u8(snap.match.phase & 0xff);
  w.u16(quantizeU16(snap.match.clock, CLOCK_RANGE));
  w.u8(Math.min(255, Math.max(0, Math.round(snap.match.score[0]))));
  w.u8(Math.min(255, Math.max(0, Math.round(snap.match.score[1]))));

  w.u8(snap.padChanges.length);
  for (const pad of snap.padChanges) {
    if (pad.index > 63 || pad.index < 0) {
      throw new Error(`boost pad index ${pad.index} does not fit the 6-bit field`);
    }
    const timer = quantizeU16(pad.respawnTimer, PAD_TIMER_RANGE);
    w.u8(pad.index | (pad.active ? 0x40 : 0) | (timer !== 0 ? 0x80 : 0));
    if (timer !== 0) w.u16(timer);
  }

  return w.finish();
};

export const decodeSnapshot = (buf: ArrayBuffer): Snapshot => {
  const r = new Reader(buf);
  const type = r.u8();
  if (type !== ServerMessage.Snapshot) throw new Error(`not a snapshot frame: type ${type}`);

  const flags = r.u8();
  const keyframe = (flags & SNAP_KEYFRAME) !== 0;
  const tick = r.u32();
  const baseTick = keyframe ? -1 : r.u32();
  const ackedInputSeq = r.i32();
  const carCount = r.u8();

  const cars: DeltaCarSnapshot[] = [];
  for (let i = 0; i < carCount; i++) {
    const mask = keyframe ? CAR_ALL : r.u16();
    const position = readVec(r, POSITION_RANGE, mask, CAR_POSITION_X);
    const velocity = readVec(r, VELOCITY_RANGE, mask, CAR_VELOCITY_X);
    const angularVelocity = readVec(r, ANGULAR_RANGE, mask, CAR_ANGULAR_X);
    const orientation: [number, number, number, number] =
      mask & CAR_ORIENTATION ? unpackQuat(r.u32()) : [0, 0, 0, 1];
    const boost = mask & CAR_BOOST ? dequantizeU8(r.u8(), BOOST_RANGE) : 0;
    const carFlags = mask & CAR_FLAGS ? r.u8() : 0;
    const car: DeltaCarSnapshot = {
      position,
      velocity,
      angularVelocity,
      orientation,
      boost,
      flags: carFlags,
    };
    if (!keyframe) car.changed = mask;
    cars.push(car);
  }

  const bmask = keyframe ? BALL_ALL : r.u16();
  const ball: BodySnapshot = {
    position: readVec(r, POSITION_RANGE, bmask, BALL_POSITION_X),
    velocity: readVec(r, VELOCITY_RANGE, bmask, BALL_VELOCITY_X),
    angularVelocity: readVec(r, ANGULAR_RANGE, bmask, BALL_ANGULAR_X),
  };

  const phase = r.u8();
  const clock = dequantizeU16(r.u16(), CLOCK_RANGE);
  const score: [number, number] = [r.u8(), r.u8()];

  const padCount = r.u8();
  const padChanges: { index: number; active: boolean; respawnTimer: number }[] = [];
  for (let i = 0; i < padCount; i++) {
    const head = r.u8();
    const timer = head & 0x80 ? dequantizeU16(r.u16(), PAD_TIMER_RANGE) : 0;
    padChanges.push({ index: head & 0x3f, active: (head & 0x40) !== 0, respawnTimer: timer });
  }

  const snap: DeltaSnapshot = {
    tick,
    baseTick,
    ackedInputSeq,
    cars,
    ball,
    padChanges,
    match: { phase, clock, score },
  };
  if (!keyframe) snap.ballChanged = bmask;
  return snap;
};

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/**
 * Encode an input packet. Layout:
 *
 *   u8   type = ClientMessage.Input
 *   u32  seq        sequence of the NEWEST input
 *   u32  tick       client tick of the NEWEST input
 *   u8   count      <= INPUT_REDUNDANCY
 *   per input, OLDEST FIRST (so the newest is last, matching `InputPacket`):
 *     i8   throttle, steer, pitch, yaw, roll   over [-1, 1]
 *     u8   buttons  jump | boost | handbrake
 *
 * Six bytes per input. A full redundancy run of 6 is 46 bytes, which at one
 * packet per tick is 5.5 kB/s upstream — the cheapest insurance in the protocol
 * and the reason a lost packet costs a player nothing.
 */
export const encodeInputPacket = (pkt: InputPacket): ArrayBuffer => {
  const w = new Writer(10 + pkt.inputs.length * 6);
  w.u8(ClientMessage.Input);
  w.u32(pkt.seq);
  w.u32(pkt.tick);
  w.u8(pkt.inputs.length);
  for (const input of pkt.inputs) {
    w.i8(quantizeAxis(input.throttle));
    w.i8(quantizeAxis(input.steer));
    w.i8(quantizeAxis(input.pitch));
    w.i8(quantizeAxis(input.yaw));
    w.i8(quantizeAxis(input.roll));
    w.u8(
      (input.jump ? BUTTON_JUMP : 0) |
        (input.boost ? BUTTON_BOOST : 0) |
        (input.handbrake ? BUTTON_HANDBRAKE : 0)
    );
  }
  return w.finish();
};

export const decodeInputPacket = (buf: ArrayBuffer): InputPacket => {
  const r = new Reader(buf);
  const type = r.u8();
  if (type !== ClientMessage.Input) throw new Error(`not an input frame: type ${type}`);

  const seq = r.u32();
  const tick = r.u32();
  const count = r.u8();
  const inputs: CarInput[] = [];
  for (let i = 0; i < count; i++) {
    const throttle = dequantizeAxis(r.i8());
    const steer = dequantizeAxis(r.i8());
    const pitch = dequantizeAxis(r.i8());
    const yaw = dequantizeAxis(r.i8());
    const roll = dequantizeAxis(r.i8());
    const buttons = r.u8();
    inputs.push({
      throttle,
      steer,
      pitch,
      yaw,
      roll,
      jump: (buttons & BUTTON_JUMP) !== 0,
      boost: (buttons & BUTTON_BOOST) !== 0,
      handbrake: (buttons & BUTTON_HANDBRAKE) !== 0,
    });
  }
  return { seq, tick, inputs };
};

// ---------------------------------------------------------------------------
// Control messages
// ---------------------------------------------------------------------------

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/**
 * A control frame: `u8 type`, `u32 byteLength`, then UTF-8 JSON.
 *
 * JSON on purpose — see the header. The explicit length is what makes the
 * envelope a FRAME rather than "the rest of the buffer": it lets a future
 * transport coalesce several messages into one datagram without this codec
 * changing, and it fails loudly on a truncated frame instead of handing
 * `JSON.parse` a half-object.
 */
export const encodeControl = (type: number, payload: unknown): ArrayBuffer => {
  const body = encoder.encode(JSON.stringify(payload ?? null));
  const w = new Writer(5 + body.length);
  w.u8(type);
  w.u32(body.length);
  w.raw(body);
  return w.finish();
};

export const decodeControl = (buf: ArrayBuffer): { type: number; payload: unknown } => {
  const r = new Reader(buf);
  const type = r.u8();
  const length = r.u32();
  if (5 + length > buf.byteLength) {
    throw new Error(`control frame truncated: want ${length} body bytes, have ${buf.byteLength - 5}`);
  }
  const payload: unknown = JSON.parse(decoder.decode(r.raw(length)));
  return { type, payload };
};
